import type {
  BlockResponseDetails,
  FileUploadBlockResponseDetails,
  FlowPostResponse,
  OpenEndedBlockResponseDetails,
  ScaleBlockLabels,
} from '@assembly-web/services';
import {
  AssemblyCurrencyType,
  BlockResponseState,
  BlockTypes,
  MemberState,
  ScaleBlockLabelType,
} from '@assembly-web/services';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Avatar, AvatarSize } from '../../../DesignSystem/Feedback/Avatar';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import type { ToolbarItem } from '../../../DesignSystem/Feedback/Toolbar';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { FileBlock } from './FileBlock';
import { getFormattedMessage } from './openEndedUtils';
import type { PostCardProps } from './PostCard';
import { PostCardActions } from './PostCard';
import { mapHexCodeToEmoticon } from './PostCardHeader';

const messages = defineMessages({
  noAnswer: {
    defaultMessage: 'No answer',
    id: 'Gw6A+4',
  },
  attachment: {
    defaultMessage: `{count, plural, =0 {} one {attachment} other {attachments}}`,
    id: 'pk+Fyy',
  },
  filesDeleted: {
    defaultMessage: 'Files(s) deleted',
    id: 'RA/vS9',
  },
  edited: {
    defaultMessage: 'edited',
    id: 'Zx1w1e',
  },
  deactivatedUser: {
    defaultMessage: '{firstName} {lastName} (deactivated)',
    id: 'tR9Liu',
  },
  cantFindTask: {
    defaultMessage: `Can’t find task`,
    id: 'MVp6ju',
  },
});

function GifBlock({ gifURL }: { gifURL: string }) {
  return (
    <div
      data-chromatic="ignore"
      className="relative z-0 h-[244px] overflow-hidden rounded text-sm text-gray-9"
    >
      <div
        style={{
          backgroundImage: 'url(' + gifURL + ')',
        }}
        className="absolute	z-[-1] h-full w-full bg-cover bg-no-repeat blur-[23px]"
        data-chromatic="ignore"
      />
      <img
        className="inset-x-0 top-0 mx-auto my-0 h-full object-contain"
        src={gifURL}
        alt=""
        data-chromatic="ignore"
      />
    </div>
  );
}

function NoAnswerBlock() {
  const { formatMessage } = useIntl();

  return (
    <div className="text-sm text-gray-8">
      <span>{formatMessage(messages.noAnswer)}</span>
    </div>
  );
}

function ScaleBlock({
  min,
  max,
  value,
  labels,
  blockId,
  showInCarousel,
}: {
  min: number;
  max: number;
  value: number;
  labels?: ScaleBlockLabels;
  blockId: string;
  showInCarousel?: boolean;
}) {
  return (
    <>
      <div
        className={classNames(
          'flex w-full grow',
          showInCarousel ? 'gap-1' : 'gap-2'
        )}
      >
        {Array.from(
          { length: max + 1 - min },
          (_, scaleIterable) => scaleIterable + min
        ).map((scaleIterable) => (
          <span
            className={classNames(
              'flex-[1] rounded py-1 text-center',
              scaleIterable === value
                ? 'bg-primary-2 text-primary-6'
                : 'bg-gray-2 text-gray-9'
            )}
            key={`scale-block-label-${blockId}-${scaleIterable}`}
          >
            {scaleIterable}
          </span>
        ))}
      </div>
      <div className="flex w-full grow gap-1">
        {Object.values(ScaleBlockLabelType).map((labelName) => {
          return (
            <span
              className={classNames(
                'flex-[1] rounded py-1 text-xs text-gray-9',
                {
                  'text-left': labelName === ScaleBlockLabelType.Low,
                  'text-center': labelName === ScaleBlockLabelType.Middle,
                  'text-end': labelName === ScaleBlockLabelType.High,
                }
              )}
              key={labelName}
            >
              {labels ? labels[labelName] ?? '' : ''}
            </span>
          );
        })}
      </div>
    </>
  );
}

function CollapsibleFileBlock({
  responseBlock,
  onFileClick,
  onToolbarItemClick,
  showInCarousel,
}: {
  responseBlock: FileUploadBlockResponseDetails | OpenEndedBlockResponseDetails;
  onFileClick: (responseBlock: { blockId: string; fileName: string }) => void;
  onToolbarItemClick?:
    | (({ args, fileName }: { args: ToolbarItem; fileName: string }) => void)
    | undefined;
  showInCarousel?: boolean;
}) {
  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(true);
  const handleFileClick = (fileName: string) => {
    onFileClick({ blockId: responseBlock.blockId, fileName });
  };
  return responseBlock.response.files ? (
    <div>
      {!showInCarousel && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center pb-2 text-xs text-gray-8"
        >
          {isOpen ? 'Hide' : 'Show'} {responseBlock.response.files.length}{' '}
          {formatMessage(messages.attachment, {
            count: responseBlock.response.files.length,
          })}
          <ChevronDownIcon
            className={classNames('ml-1 inline h-4 w-4', {
              'rotate-180 transform': isOpen,
            })}
          />
        </button>
      )}
      <div
        className={classNames('grid text-sm text-gray-9 transition-all', {
          'grid-rows-[1fr]': isOpen,
          'grid-rows-[0fr]': !isOpen,
        })}
      >
        <FileBlock
          files={responseBlock.response.files}
          onFileClick={handleFileClick}
          onToolbarItemClick={onToolbarItemClick}
          showInCarousel={showInCarousel}
        />
      </div>
    </div>
  ) : null;
}

function BlockContent({
  responseBlock,
  responseId,
  onPostCardBodyClick,
  currency,
  onFileClick,
  showEdit,
  onToolbarItemClick,
  showInCarousel,
}: {
  responseBlock: BlockResponseDetails;
  responseId: string;
  onPostCardBodyClick: PostCardProps['onPostCardBodyClick'];
  currency: PostCardProps['currency'];
  onFileClick: (responseBlock: { blockId: string; fileName: string }) => void;
  showEdit: boolean;
  onToolbarItemClick?: (responseBlock: {
    blockId: string;
    fileName: string;
    itemId: string;
  }) => void;
  showInCarousel?: boolean;
}) {
  const { formatMessage } = useIntl();

  const handleToolbarItemClick = ({
    args,
    fileName,
  }: {
    args: ToolbarItem;
    fileName: string;
  }) => {
    onToolbarItemClick?.({
      blockId: responseBlock.blockId,
      fileName,
      itemId: args.id,
    });
  };

  const editTag = showEdit ? (
    <TextStyle as="span" variant="xs-regular" subdued>
      ({formatMessage(messages.edited)})
    </TextStyle>
  ) : null;

  if (responseBlock.state === BlockResponseState.Skipped) {
    if (
      responseBlock.type === BlockTypes.FileUpload &&
      showEdit &&
      responseBlock.edited
    ) {
      return (
        <TextStyle className="text-gray-8">
          {formatMessage(messages.filesDeleted)}
          {editTag}
        </TextStyle>
      );
    }
    return <NoAnswerBlock />;
  }

  switch (responseBlock.type) {
    case BlockTypes.MultiChoice:
    case BlockTypes.Dropdown:
      return (
        <div className="text-sm text-gray-9">
          {responseBlock.response.value.map((i) => (
            <p key={`${responseId}-${responseBlock.blockId}-${i.id}`}>
              {' '}
              {i.value} {responseBlock.edited && editTag}
            </p>
          ))}
        </div>
      );

    case BlockTypes.Gif:
      return (
        <>
          <GifBlock gifURL={responseBlock.response.value} />
          {responseBlock.edited && editTag}
        </>
      );

    case BlockTypes.FileUpload:
      return responseBlock.response.files.length > 0 ? (
        <>
          <CollapsibleFileBlock
            onFileClick={onFileClick}
            responseBlock={responseBlock}
            onToolbarItemClick={handleToolbarItemClick}
            showInCarousel={showInCarousel}
          />
          {responseBlock.edited && editTag}
        </>
      ) : (
        <NoAnswerBlock />
      );

    case BlockTypes.GivePointsStack: {
      const currencyIcon =
        currency.type === AssemblyCurrencyType.Custom ? (
          <img
            alt={currency.name}
            className="mr-0.5 h-3 w-3"
            src={currency.value}
          />
        ) : (
          mapHexCodeToEmoticon(currency.value)
        );

      return (
        <div className="flex flex-row items-center text-sm text-gray-9">
          {currencyIcon} {responseBlock.response.value}
        </div>
      );
    }

    case BlockTypes.OpenEnded:
      return (
        <div className="flex flex-col gap-2 text-sm text-gray-9">
          <div
            className={classNames('gap-1 [overflow-wrap:anywhere]', {
              'my-1 leading-6': !showInCarousel,
              'leading-[22px]': showInCarousel,
            })}
          >
            {getFormattedMessage(
              responseBlock.response,
              onPostCardBodyClick,
              {}
            )}{' '}
            {responseBlock.edited && editTag}
          </div>
          {responseBlock.response.files &&
            responseBlock.response.files.length > 0 && (
              <CollapsibleFileBlock
                onFileClick={onFileClick}
                responseBlock={responseBlock}
                onToolbarItemClick={handleToolbarItemClick}
              />
            )}
          {responseBlock.response.gifUrl && (
            <GifBlock gifURL={responseBlock.response.gifUrl} />
          )}
        </div>
      );

    case BlockTypes.PersonSelector:
      return (
        <>
          <div className="flex flex-wrap gap-2 text-sm text-gray-9">
            {responseBlock.response.persons.map((person) => {
              if (person.memberState === MemberState.Deactivated) {
                return (
                  <div
                    key={`${responseId}-${responseBlock.blockId}-${person.memberId}`}
                    className="mt-1 flex w-fit items-center gap-2 rounded-2xl bg-gray-2 p-1 pr-4"
                  >
                    <Avatar
                      className="grayscale"
                      image={person.image}
                      memberID={person.memberId}
                      name={person.name.firstName.toString()}
                      size={AvatarSize.Small}
                    />
                    <TextStyle variant="sm-regular" className="text-gray-8">
                      {formatMessage(messages.deactivatedUser, {
                        firstName: person.name.firstName,
                        lastName: person.name.lastName,
                      })}
                    </TextStyle>
                  </div>
                );
              } else {
                return (
                  <button
                    key={`${responseId}-${responseBlock.blockId}-${person.memberId}`}
                    onClick={() => {
                      onPostCardBodyClick(
                        PostCardActions.PersonClicked,
                        person.memberId
                      );
                    }}
                    className="mt-1 flex w-fit items-center gap-2 rounded-2xl bg-gray-3 py-1 pl-1 pr-2 text-sm text-gray-9 hover:bg-gray-4"
                  >
                    <Avatar
                      image={person.image}
                      memberID={person.memberId}
                      name={person.name.firstName.toString()}
                      size={AvatarSize.Small}
                    />
                    {`${person.name.firstName.toString()} ${(
                      person.name.lastName ?? ''
                    ).toString()}`}
                  </button>
                );
              }
            })}
          </div>
          {responseBlock.edited && editTag}
        </>
      );

    case BlockTypes.Scale:
      return (
        <>
          <ScaleBlock
            min={responseBlock.content.min}
            max={responseBlock.content.max}
            value={responseBlock.response.value}
            labels={responseBlock.content.labels}
            blockId={responseBlock.blockId}
            showInCarousel={showInCarousel}
          />
          {responseBlock.edited && editTag}
        </>
      );

    default:
      return null;
  }
}

export function PostCardBody({
  flowResponse,
  onPostCardBodyClick,
  currency,
  onFileClick,
  onToolbarItemClick,
  showInCarousel = false,
}: {
  flowResponse: FlowPostResponse;
  onPostCardBodyClick: PostCardProps['onPostCardBodyClick'];
  currency: PostCardProps['currency'];
  onFileClick: (responseBlock: { blockId: string; fileName: string }) => void;
  onToolbarItemClick?: (responseBlock: {
    blockId: string;
    fileName: string;
    itemId: string;
  }) => void;
  showInCarousel?: boolean;
}) {
  return (
    <section className="mb-2">
      {flowResponse.responses.map((responseBlock) => (
        <div
          className={classNames('text-left', {
            'my-4': !showInCarousel,
            'pt-2': showInCarousel,
          })}
          key={`${responseBlock.blockId}-${responseBlock.content.title}`}
        >
          <div className="text-xs text-gray-8">
            {responseBlock.content.title}
          </div>
          <BlockContent
            responseId={flowResponse.responseId}
            responseBlock={responseBlock}
            onPostCardBodyClick={onPostCardBodyClick}
            currency={currency}
            onFileClick={onFileClick}
            showEdit={flowResponse.flow.flowId !== 'recognition'}
            onToolbarItemClick={onToolbarItemClick}
            showInCarousel={showInCarousel}
          />
        </div>
      ))}
    </section>
  );
}
